import ReactApexChart from 'react-apexcharts'
import { useStatsAllProjects } from '../../../../hooks/useStatsAllProjects'
import { getExpectedRandoesProjectionSeries, repeat, SeriesForPlot, toRelativePercentage } from '../../utils'

export const ProjectsTimelineChart = ({
  visibilityPerProject,
  setVisibilityPerProject,
}: {
  visibilityPerProject: boolean[]
  setVisibilityPerProject: React.Dispatch<React.SetStateAction<boolean[]>>
}) => {
  const { statsPerProject } = useStatsAllProjects()

  if (!statsPerProject) return null

  const goalPostfix = ' goal' as const
  const forecastPostfix = ' forecast' as const

  const series: {
    name: string
    data: SeriesForPlot
    width?: number
    dash?: number
    legendShape?: string
    projectStats: typeof statsPerProject[0]
  }[] = statsPerProject
    .map((projectStats) => {
      // actual randomizations
      return {
        name: projectStats.projectTitle,
        data: toRelativePercentage(projectStats.randoTimelineSeries, projectStats.relativeToGoalPercentage),
        color: projectStats.color,
        projectStats,
      }
    })
    .concat(
      statsPerProject
        .filter((projectStats, idx) => visibilityPerProject[idx])
        .map((projectStats, idx) => {
          // projections
          const data = getExpectedRandoesProjectionSeries(
            projectStats.randoTimelineSeries,
            projectStats.expectedRandomizations
          )

          return {
            name: projectStats.projectTitle + forecastPostfix,
            data: toRelativePercentage(data, projectStats.relativeToGoalPercentage),
            type: 'line',
            zIndex: 3,
            dash: 3,
            //hidden: !visibilityPerProject[idx],
            legendShape: 'plus',
            color: projectStats.color,
            projectStats,
          }
        })
    )
    .concat(
      statsPerProject
        .filter((projectStats, idx) => visibilityPerProject[idx])
        .filter((projectStats) => projectStats.newestGoal)
        .map((projectStats, idx) => {
          // goals
          return {
            name: projectStats.projectTitle + goalPostfix,
            data: [[projectStats.newestGoal!.deadline, 100]],
            type: 'line',
            //hidden: !visibilityPerProject[idx],
            zIndex: 3,
            dash: 3,
            legendShape: 'plus',
            color: projectStats.color,
            projectStats,
          }
        })
    )

  const options = {
    chart: {
      id: 'area-datetime',
      type: 'line',
      height: 500,
      events: {
        legendClick: function (chartContext, seriesIndex, config) {
          const newVisibility = [...visibilityPerProject]
          newVisibility[seriesIndex] = !newVisibility[seriesIndex] // toggle it
          setVisibilityPerProject(newVisibility)
        },
      },
      /* zoom: {
          autoScaleYaxis: true,
        }, */
      toolbar: {
        show: false,
      },
    },
    dataLabels: {
      enabled: true,
      formatter: (value, { seriesIndex, dataPointIndex, w }) => {
        // We only want to label the last value in the series.
        if (dataPointIndex !== w.globals.series[seriesIndex].length - 1) {
          return ''
        }

        const seriesName: string = w.globals.seriesNames[seriesIndex]
        const currentSeries = series.find((s) => s.name === seriesName)

        // dont show in percentage. We want the real value now. OGSÅ FOR SELVE LABEL!!!!!
        const realVal = currentSeries?.projectStats
          ? Math.round(value / currentSeries.projectStats.relativeToGoalPercentage)
          : '??'

        let postfix = ''

        const isGoalSeries = seriesName.includes(goalPostfix)
        if (isGoalSeries) {
          const goalType = currentSeries?.projectStats.newestGoal.goalType
          if (goalType === 'Or more') postfix = '+'
          if (goalType === 'Or less') postfix = '-'
        }

        return `${realVal}${postfix}`
      },
    },
    markers: {
      size: 0,
      style: 'hollow',
    },
    legend: {
      horizontalAlign: 'left',
      show: true,
      position: 'top',
      offsetY: -5,
      markers: {
        shape: series.map((serie) => ('legendShape' in serie && serie.legendShape ? serie.legendShape : 'circle')),
        fillColors: statsPerProject.map((p) => p.color).concat(repeat(['#FFF'], 50)),
      },
      formatter: (value, { seriesIndex, dataPointIndex, w }) => {
        const seriesName = w.globals.seriesNames[seriesIndex]
        if (seriesName.includes(forecastPostfix) || seriesName.includes(goalPostfix)) return ''

        return value
      },
    },
    yaxis: {
      labels: {
        formatter: function (value) {
          return value + ' %'
        },
      },
    },
    xaxis: {
      type: 'datetime',
      //min: new Date('01 Jan 2024').getTime(),
      //max: newestGoal?.deadline && !showOldRandoGoals ? new Date(newestGoal.deadline).getTime() : Date.now(),
      tickAmount: 6,
    },
    annotations: {
      yaxis: [
        {
          y: 100,
          yAxisIndex: 0,
          borderColor: '#DAA520',
          label: {
            borderColor: '#DAA520',
            style: {
              color: '#fff',
              background: '#DAA520',
            },
            text: '',
          },
        },
      ],
    },
    tooltip: {
      x: {
        format: 'dd MMM yyyy',
      },
      y: {
        formatter: (value, { seriesIndex, dataPointIndex, w }) => {
          const seriesName: string = w.globals.seriesNames[seriesIndex]
          const currentSeries = series.find((s) => s.name === seriesName)

          // dont show in percentage. We want the real value now. OGSÅ FOR SELVE LABEL!!!!!
          const realVal = currentSeries?.projectStats
            ? Math.round(value / currentSeries.projectStats.relativeToGoalPercentage)
            : '??'

          const isGoalSeries = seriesName.includes(goalPostfix)
          const postfix = isGoalSeries ? `${currentSeries?.projectStats.newestGoal.goalType}` : `(${value}% of goal)`

          return `${realVal} ${postfix}`
        },
      },
    },
    title: {
      text: 'Timeline',
      align: 'left',
    },
    stroke: {
      width: series.map((serie) => ('width' in serie && serie.width ? serie.width : 4)),
      curve: 'straight',
      dashArray: series.map((serie) => ('dash' in serie && serie.dash ? serie.dash : 0)),
    },
  }

  return (
    <div>
      <div className="chart">
        <div id="chart-timeline">
          {/*@ts-ignore */}
          <ReactApexChart options={options} series={series} type="line" height={500} />
        </div>
      </div>
      <div id="html-dist"></div>
    </div>
  )
}
